import { CustomColumnProps } from '@ihsm/ui-shared';
import { has } from 'lodash';
import { HeaderTabItemProps } from '@ihsm/ui-shared/lib/typings/shared/components/HeaderTabs/components/HeaderTabItem';
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import {
  useGetReportsReportDataQuery,
  useGetReportSummaryDataQuery,
} from 'services/newApi/reportsDataApi';
import { useAppSelector } from 'state/store';
import { currentReportSelector } from 'state/store/selectors';
import { useHeaderTabs } from '.';

type UseReportsReportData = {
  headerTabs: HeaderTabItemProps[];
  dataColumns: CustomColumnProps[];
  showLoading: boolean;
  isError: boolean;
  reportDataList: unknown[];
  fundsList: string[];
  selectedFund: string;
  setSelectedFund: Dispatch<SetStateAction<string>>;
};

export const useReportsReportData = (): UseReportsReportData => {
  const headerTabs = useHeaderTabs();
  const { selectedContext } = useAppSelector((state) => state.contextsState);
  const currentReport = currentReportSelector(useAppSelector((state) => state));

  const [selectedFund, setSelectedFund] = useState<string>('');
  const [skip, setSkip] = useState<boolean>(true);
  const [skipReports, setSkipReports] = useState<boolean>(true);
  const [dataColumns, setDataColumns] = useState<CustomColumnProps[]>([]);
  const [showLoading, setShowLoading] = useState<boolean>(false);

  const {
    data: summaryDataList = [],
    isError: isErrorSummary,
    isLoading: isLoadingSummary,
  } = useGetReportSummaryDataQuery(
    {
      selectedContext,
      selectedReport: currentReport?.name ?? '',
    },
    { skip }
  );

  const fundsList = useMemo(() => {
    return summaryDataList.map(({ fundName }) => {
      return fundName;
    });
  }, [summaryDataList]);

  const {
    data: reportDataList = [],
    isError,
    isLoading,
  } = useGetReportsReportDataQuery(
    {
      selectedContext,
      selectedReport: currentReport?.name ?? '',
      selectedFund,
    },
    { skip: skipReports && !!fundsList }
  );

  // Once there is context and report are selected - SummaryData should start loading
  useEffect(() => {
    if (selectedContext && currentReport && skip) {
      setSkip(false);
      setShowLoading(true);
    }
  }, [selectedContext, currentReport, skip, setSkip, setShowLoading]);

  // Once summary data has been loaded - report data should start loading
  useEffect(() => {
    if ((summaryDataList?.length && !isLoadingSummary) || isErrorSummary) {
      if (!isErrorSummary) {
        setSelectedFund(summaryDataList[0].fundName);
      }
      setSkipReports(false);
    }
  }, [setShowLoading, summaryDataList, isLoadingSummary, isErrorSummary]);

  // Report data loading has been finished
  useEffect(() => {
    if ((reportDataList?.length && !isLoading) || isError)
      setShowLoading(false);
  }, [isError, isLoading, setShowLoading, reportDataList?.length]);

  // once report data has been loaded - create column list
  useEffect(() => {
    const isReportDataAvailable =
      Array.isArray(reportDataList) && reportDataList.length > 0;

    if (!isReportDataAvailable) {
      return;
    }

    const isCurrentReportAvailable =
      currentReport &&
      Array.isArray(currentReport.columns) &&
      currentReport.columns.length > 0;

    const headerColumnsRow: CustomColumnProps[] = [];

    // `currentReport` is required on this block to ensure TS that this is a valid object
    if (currentReport && isCurrentReportAvailable) {
      const sorted = [...currentReport.columns].sort((a, b) => a.sort - b.sort);

      sorted.forEach((column) => {
        const firstElement = summaryDataList[0];

        if (has(firstElement.results, column.name)) {
          headerColumnsRow.push({
            name: column.name,
            title: column.name,
            columnType: 'default',
          });
        }
      });
    }

    setDataColumns(headerColumnsRow);
  }, [currentReport, reportDataList, summaryDataList]);

  return {
    showLoading,
    headerTabs,
    dataColumns,
    reportDataList,
    fundsList,
    selectedFund,
    setSelectedFund,
    isError: isError || isErrorSummary,
  };
};
